/*!
 * Girlbasket Theme v1.0.0
 * Copyright (c) 2026 Om. All rights reserved.
 *
 * This file is part of the Girlbasket theme, licensed only for use on the
 * Girlbasket store (girlbasket.myshopify.com / girlbasket.com). Copying,
 * modifying, redistributing or reselling this file, in whole or in part,
 * without written permission from Om is prohibited. See LICENSE.md.
 *
 * File: assets/gb-cart.js
 */
(() => {
  'use strict';

  const GB = window.GB;
  if (!GB || !GB.events) return;

  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  /* ---------------------------------------------------------------------
     GB.cart: Cart API helpers. Every change re-renders the cart sections
     on the page (drawer and cart page) and fires "gb:cart:updated".
     --------------------------------------------------------------------- */
  GB.cart = {
    sectionIds() {
      return [...document.querySelectorAll('gb-cart[data-section-id]')].map((el) => el.dataset.sectionId);
    },

    async get() {
      const response = await fetch(`${GB.routes.root.replace(/\/$/, '')}/cart.js`, { headers: { Accept: 'application/json' } });
      return response.json();
    },

    async add(formData) {
      const ids = this.sectionIds();
      if (ids.length) {
        formData.append('sections', ids.join(','));
        formData.append('sections_url', window.location.pathname);
      }
      const response = await fetch(GB.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || GB.strings.cartError);
      }
      if (data.sections) this.renderSections(data.sections);
      const cart = await this.get();
      GB.events.emit('cart:updated', { cart, source: 'add' });
      this.afterAdd(data, cart);
      return data;
    },

    async change(line, quantity) {
      const ids = this.sectionIds();
      const response = await fetch(GB.routes.cartChange, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ line, quantity, sections: ids, sections_url: window.location.pathname })
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || GB.strings.cartError);
      }
      if (data.sections) this.renderSections(data.sections);
      GB.events.emit('cart:updated', { cart: data, source: 'change' });
      return data;
    },

    async updateNote(note) {
      await fetch(GB.routes.cartUpdate, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ note })
      });
    },

    renderSections(sections) {
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const target = document.querySelector(`gb-cart[data-section-id="${CSS.escape(id)}"]`);
        const fresh = GB.parseHtml(html).querySelector('gb-cart');
        if (target && fresh) target.innerHTML = fresh.innerHTML;
      });
    },

    afterAdd(item, cart) {
      const drawer = document.getElementById('GbCartDrawer');
      switch (GB.settings.cartAfterAdd) {
        case 'checkout':
          window.location.href = `${GB.routes.root.replace(/\/$/, '')}/checkout`;
          break;
        case 'notification':
          showNotice(item, cart);
          break;
        default:
          if (drawer && !document.body.classList.contains('template-cart')) drawer.open();
          else showNotice(item, cart);
      }
    }
  };

  /* ---------------------------------------------------------------------
     <gb-cart>: line quantities, removal, note and terms
     --------------------------------------------------------------------- */
  class GbCart extends HTMLElement {
    connectedCallback() {
      this.queue = Promise.resolve();

      this.addEventListener(
        'change',
        GB.debounce((event) => {
          const input = event.target.closest('[data-gb-line-qty]');
          if (input) this.update(Number(input.dataset.gbLineQty), Number(input.value));
        }, 350)
      );

      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-gb-line-remove]');
        if (!remove) return;
        event.preventDefault();
        this.update(Number(remove.dataset.gbLineRemove), 0);
      });

      this.addEventListener(
        'input',
        GB.debounce((event) => {
          if (event.target.matches('[data-gb-cart-note]')) GB.cart.updateNote(event.target.value);
        }, 500)
      );

      this.addEventListener('click', (event) => {
        const checkout = event.target.closest('[data-gb-checkout]');
        if (!checkout) return;
        const terms = this.querySelector('[data-gb-terms]');
        const error = this.querySelector('[data-gb-terms-error]');
        if (terms && !terms.checked) {
          event.preventDefault();
          if (error) error.hidden = false;
          terms.focus();
        }
      });

      this.addEventListener('change', (event) => {
        if (!event.target.matches('[data-gb-terms]')) return;
        const error = this.querySelector('[data-gb-terms-error]');
        if (error && event.target.checked) error.hidden = true;
      });

      // Recommended products added straight from the drawer.
      this.addEventListener('submit', async (event) => {
        const form = event.target.closest('.gb-cart__rec-form');
        if (!form) return;
        event.preventDefault();
        const button = form.querySelector('button');
        button.classList.add('is-loading');
        try {
          await GB.cart.add(new FormData(form));
        } catch (error) {
          this.showError(error.message);
        } finally {
          button.classList.remove('is-loading');
        }
      });
    }

    update(line, quantity) {
      this.queue = this.queue.then(async () => {
        this.classList.add('is-updating');
        const lineEl = this.querySelector(`[data-gb-line="${line}"]`);
        if (lineEl) lineEl.classList.add('is-loading');
        try {
          await GB.cart.change(line, quantity);
        } catch (error) {
          this.showError(error.message);
          const cart = await GB.cart.get();
          GB.events.emit('cart:updated', { cart, source: 'error' });
          this.refresh();
        } finally {
          this.classList.remove('is-updating');
        }
      });
    }

    async refresh() {
      const id = this.dataset.sectionId;
      const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(id)}`);
      GB.cart.renderSections({ [id]: await response.text() });
    }

    showError(message) {
      const box = this.querySelector('[data-gb-cart-error]');
      if (!box) {
        GB.toast(message || GB.strings.cartError, { type: 'error' });
        return;
      }
      box.textContent = message || GB.strings.cartError;
      box.hidden = false;
      setTimeout(() => {
        box.hidden = true;
      }, 6000);
    }
  }
  customElements.define('gb-cart', GbCart);

  /* ---------------------------------------------------------------------
     The header cart icon opens the drawer
     --------------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('#GbCartLink');
    const drawer = document.getElementById('GbCartDrawer');
    if (!link || !drawer || document.body.classList.contains('template-cart')) return;
    event.preventDefault();
    drawer.open(link);
  });

  GB.events.on('cart:updated', ({ cart }) => {
    document.querySelectorAll('[data-gb-cart-count-text]').forEach((el) => {
      el.textContent = cart.item_count;
    });
  });

  /* ---------------------------------------------------------------------
     <gb-cart-notification>: "Added to cart" pop-over
     --------------------------------------------------------------------- */
  class GbCartNotification extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-gb-notice-close]')?.addEventListener('click', () => this.hide());
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.hide();
      });
    }

    show(item) {
      const target = this.querySelector('[data-gb-notice-item]');
      if (target && item) {
        target.innerHTML = '';
        const image = item.featured_image && item.featured_image.url;
        if (image) {
          const img = document.createElement('img');
          img.src = `${image}${image.includes('?') ? '&' : '?'}width=160`;
          img.alt = '';
          img.width = 64;
          img.height = 64;
          target.appendChild(img);
        }
        const text = document.createElement('div');
        const title = document.createElement('p');
        title.className = 'gb-cart-notice__product';
        title.textContent = item.product_title;
        text.appendChild(title);
        if (item.variant_title && !item.product_has_only_default_variant) {
          const variant = document.createElement('p');
          variant.className = 'gb-subtle';
          variant.textContent = item.variant_title;
          text.appendChild(variant);
        }
        const price = document.createElement('p');
        price.textContent = GB.formatMoney(item.final_line_price);
        text.appendChild(price);
        target.appendChild(text);
      }
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-visible'));
      this.focus({ preventScroll: true });
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.hide(), 6000);
    }

    hide() {
      this.classList.remove('is-visible');
      setTimeout(() => {
        this.hidden = true;
      }, 250);
    }
  }
  customElements.define('gb-cart-notification', GbCartNotification);

  function showNotice(item) {
    const notice = document.querySelector('gb-cart-notification');
    if (notice) notice.show(item);
    else GB.toast(GB.strings.added);
  }

  /* ---------------------------------------------------------------------
     <gb-shipping-calc>: estimates rates for an address
     --------------------------------------------------------------------- */
  class GbShippingCalc extends HTMLElement {
    connectedCallback() {
      this.country = this.querySelector('#GbShipCountry');
      this.province = this.querySelector('#GbShipProvince');
      this.provinceWrap = this.querySelector('#GbShipProvinceWrap');
      this.zip = this.querySelector('#GbShipZip');
      this.result = this.querySelector('[data-gb-ship-result]');
      if (!this.country) return;

      const fallback = this.country.dataset.default;
      if (fallback) {
        const match = [...this.country.options].find((option) => option.value === fallback);
        if (match) this.country.value = fallback;
      }
      this.country.addEventListener('change', () => this.fillProvinces());
      this.fillProvinces();
      this.querySelector('[data-gb-ship-calc]').addEventListener('click', () => this.calculate());
    }

    fillProvinces() {
      const option = this.country.selectedOptions[0];
      const provinces = option ? JSON.parse(option.dataset.provinces || '[]') : [];
      this.province.innerHTML = '';
      provinces.forEach(([value, label]) => {
        const item = document.createElement('option');
        item.value = value;
        item.textContent = label;
        this.province.appendChild(item);
      });
      this.provinceWrap.hidden = provinces.length === 0;
    }

    async calculate() {
      const params = new URLSearchParams({
        'shipping_address[country]': this.country.value,
        'shipping_address[province]': this.provinceWrap.hidden ? '' : this.province.value,
        'shipping_address[zip]': this.zip.value
      });
      this.result.textContent = GB.strings.loading;
      try {
        const response = await fetch(`${GB.routes.root.replace(/\/$/, '')}/cart/shipping_rates.json?${params}`);
        const data = await response.json();
        if (!response.ok) {
          const messages = Object.entries(data).map(([field, errors]) => `${field} ${[].concat(errors).join(', ')}`);
          this.result.textContent = messages.join('. ');
          return;
        }
        const rates = data.shipping_rates || [];
        if (!rates.length) {
          this.result.textContent = GB.strings.noShippingRates;
          return;
        }
        const list = document.createElement('ul');
        list.className = 'gb-ship-calc__rates';
        rates.forEach((rate) => {
          const item = document.createElement('li');
          const cents = Math.round(parseFloat(rate.price) * 100);
          item.textContent = `${rate.name}: ${GB.formatMoney(cents)}`;
          list.appendChild(item);
        });
        this.result.textContent = GB.strings.shippingRates.replace('{{ count }}', rates.length);
        this.result.appendChild(list);
      } catch (error) {
        this.result.textContent = GB.strings.cartError;
      }
    }
  }
  customElements.define('gb-shipping-calc', GbShippingCalc);
})();
